import { useState, useEffect } from 'react'

// useState, but with local storage backup
export function useLocalStorage<T>(key: string, initialValue: T | (() => T)) {
    // gets the backup
    const getStoredValue = () => {
        // nab the local storage item
        const item = localStorage.getItem(key)
        if (item !== null) return JSON.parse(item) as T

        // fall back to passed in initial value if nothing's there
        return initialValue instanceof Function ? initialValue() : initialValue
    }

    // useState is our single source of truth
    const [value, setValue] = useState<T>(getStoredValue)

    // load from backup when key changes
    useEffect(() => {
        setValue(getStoredValue())
    }, [key])

    // sync to backup when value changes
    useEffect(() => {
        localStorage.setItem(key, JSON.stringify(value))
        window.dispatchEvent(
            new CustomEvent('local-storage-change', { detail: key })
        )
    }, [JSON.stringify(value)])

    // load from backup when another tab or same-page component updates it
    useEffect(() => {
        const handleStorage = (e: StorageEvent) => {
            if (e.key === key) setValue(getStoredValue())
        }
        const handleLocal = (e: Event) => {
            if ((e as CustomEvent).detail === key) setValue(getStoredValue())
        }
        window.addEventListener('storage', handleStorage)
        window.addEventListener('local-storage-change', handleLocal)
        return () => {
            window.removeEventListener('storage', handleStorage)
            window.removeEventListener('local-storage-change', handleLocal)
        }
    }, [key])

    return [value, setValue] as const
}
